import { PREFERENCE_MAXIMUM_LIMIT } from "@repo/shared";
import type { ReactNode } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./preference-maximum-fields.i18n";
import { readPreferenceMaximums } from "./preference-maximums";

type PreferenceMaximumFieldsProps = {
  /** Keeps the input ids unique when the fields are on the page twice. */
  idPrefix: string;
  perPreference: string;
  total: string;
  onPerPreferenceChange: (value: string) => void;
  onTotalChange: (value: string) => void;
  disabled?: boolean;
  /** Rendered in the row after the two fields, e.g. a save button. */
  children?: ReactNode;
};

/**
 * The two maximums of a dynamic portfolio's preference tilt as text fields,
 * with the reason they cannot be saved underneath.
 */
export function PreferenceMaximumFields({
  idPrefix,
  perPreference,
  total,
  onPerPreferenceChange,
  onTotalChange,
  disabled,
  children,
}: PreferenceMaximumFieldsProps) {
  const t = useT(T);
  const { error } = readPreferenceMaximums(perPreference, total);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-maxPerPreference`}>{t.maxPerPreference}</Label>
          <Input
            id={`${idPrefix}-maxPerPreference`}
            type="number"
            step="1"
            min="0"
            max={PREFERENCE_MAXIMUM_LIMIT}
            className="w-40"
            value={perPreference}
            onChange={(e) => onPerPreferenceChange(e.target.value)}
            disabled={disabled}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-maxTotalPreferences`}>{t.maxTotalPreferences}</Label>
          <Input
            id={`${idPrefix}-maxTotalPreferences`}
            type="number"
            step="1"
            min="0"
            max={PREFERENCE_MAXIMUM_LIMIT}
            className="w-40"
            value={total}
            onChange={(e) => onTotalChange(e.target.value)}
            disabled={disabled}
          />
        </div>
        {children}
      </div>
      {error && <p className="text-sm text-destructive">{t[error]}</p>}
    </div>
  );
}
